import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { Plus, Edit2, Search, Trash2 } from "lucide-react";
import {
  listCMDBApplications,
  createCMDBApplication,
  updateCMDBApplication,
  deleteCMDBApplication,
  CMDBApplication,
  ApplicationStatus,
  ApplicationCriticality,
  listOrgUnits,
  OrgUnit,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import {
  RBACButton,
  RBACAction,
  RBACMultiAction,
} from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, Column } from "../components/DataTable";
import { StatusBadge, flattenOrgHierarchy } from "./cmdb/cmdbUtils";
import { OrgCascadeFilter } from "./cmdb/components/OrgCascadeFilter";
import { Tip } from "../components/ui/tooltip";

// Redundant constants removed - using cmdbUtils

// ─── Form State ──────────────────────────────────────────────────────────────

interface AppForm {
  name: string;
  appCode: string;
  description: string;
  parentId: string;
  status: ApplicationStatus | "";
  criticality: ApplicationCriticality | "";
}

const DEFAULT_FORM: AppForm = {
  name: "",
  appCode: "",
  description: "",
  parentId: "",
  status: "",
  criticality: "",
};

// ─── Component ───────────────────────────────────────────────────────────────

export function CMDBApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CMDBApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState<CMDBApplication | null>(null);
  const [form, setForm] = useState<AppForm>(DEFAULT_FORM);

  // Delete confirmation state
  const [deletingApp, setDeletingApp] = useState<CMDBApplication | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // Create / Edit in-flight flag (block double-submit)
  const [submitting, setSubmitting] = useState(false);

  // All org units (flat) — used for both create picker and cascade filters
  const [parentOptions, setParentOptions] = useState<OrgUnit[]>([]);

  // Cascade filter state (toolbar)
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterDivId, setFilterDivId] = useState("");
  const [filterDeptId, setFilterDeptId] = useState("");

  // Cascade picker state (create form)
  const [formOrgId, setFormOrgId] = useState("");
  const [formDivId, setFormDivId] = useState("");
  const [formDeptId, setFormDeptId] = useState("");

  useEffect(() => {
    loadApplications();
    loadParentOptions();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterOrgId, filterDivId, filterDeptId]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const res = await listCMDBApplications();
      setApplications(res.data || []);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load applications",
        );
    } finally {
      setLoading(false);
    }
  };

  const loadParentOptions = async () => {
    try {
      const res = await listOrgUnits();
      setParentOptions(
        flattenOrgHierarchy(res.data || [], [
          "ORGANIZATION",
          "DIVISION",
          "DEPARTMENT",
        ]),
      );
    } catch {
      // non-fatal
    }
  };

  // ─── Open Modal ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingApp(null);
    setForm(DEFAULT_FORM);
    setFormOrgId("");
    setFormDivId("");
    setFormDeptId("");
    setShowModal(true);
  };

  const openEdit = (app: CMDBApplication) => {
    setEditingApp(app);
    setForm({
      name: app.name,
      appCode: app.appCode || "",
      description: app.description || "",
      parentId: app.parentId || "",
      status: app.status,
      criticality: app.criticality,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingApp(null);
    setForm(DEFAULT_FORM);
    setFormOrgId("");
    setFormDivId("");
    setFormDeptId("");
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (submitting) return;
    if (!form.name.trim()) {
      useToastStore
        .getState()
        .showError("Error", "Application name is required");
      return;
    }
    if (!form.appCode.trim()) {
      showToast("error", "Application code is required");
      return;
    }
    if (!form.status) {
      useToastStore.getState().showError("Error", "Please select a status");
      return;
    }
    if (!form.criticality) {
      useToastStore.getState().showError("Error", "Please select a criticality");
      return;
    }
    if (!editingApp) {
      if (!form.parentId) {
        useToastStore
          .getState()
          .showError("Error", "Please select a Department as the parent");
        return;
      }
      // Backend does not validate parent type on POST /cmdb/applications;
      // hierarchy (DEPARTMENT → APPLICATION) is only enforced on /org-units.
      const parent = parentOptions.find((o) => o.id === form.parentId);
      if (!parent || parent.orgType !== "DEPARTMENT") {
        useToastStore
          .getState()
          .showError("Error", "Application parent must be a Department");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editingApp) {
        const updated = await updateCMDBApplication(editingApp.id, {
          name: form.name,
          appCode: form.appCode || null,
          description: form.description || null,
          status: form.status as ApplicationStatus,
          criticality: form.criticality as ApplicationCriticality,
        });
        // Merge over the previous row so PATCH responses that omit derived/joined
        // fields (departmentName, divisionName, path, componentCount) don't blank
        // out columns until the next full reload.
        setApplications((prev) =>
          prev.map((a) =>
            a.id === editingApp.id ? { ...a, ...updated.data } : a,
          ),
        );
        useToastStore.getState().showSuccess("Success", "Application updated");
      } else {
        await createCMDBApplication({
          name: form.name,
          appCode: form.appCode || null,
          description: form.description || null,
          parentId: form.parentId,
          status: form.status as ApplicationStatus,
          criticality: form.criticality as ApplicationCriticality,
        });
        useToastStore.getState().showSuccess("Success", "Application created");
        closeModal();
        // Reload from the server so the new row appears with its derived/joined
        // fields (departmentName, divisionName, componentCount) and in the
        // backend's newest-first order; jump to the first page so it's visible.
        setPage(0);
        await loadApplications();
        return;
      }
      closeModal();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Operation failed",
        );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deletingApp) return;
    setDeleting(true);
    try {
      await deleteCMDBApplication(deletingApp.id);
      setApplications((prev) => prev.filter((a) => a.id !== deletingApp.id));
      useToastStore.getState().showSuccess("Success", "Application deleted.");
      setDeletingApp(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0034-E")) {
        useToastStore
          .getState()
          .showError(
            "Error",
            "This application is linked to one or more System Security Plans and cannot be deleted. Remove the SSP association first.",
          );
      } else if (msg.includes("404")) {
        useToastStore.getState().showError("Error", "Application not found.");
      } else {
        useToastStore
          .getState()
          .showError("Error", "Failed to delete application");
      }
      setDeletingApp(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Cascade derived lists ───────────────────────────────────────────────

  const orgOptions = useMemo(
    () => parentOptions.filter((o) => o.orgType === "ORGANIZATION"),
    [parentOptions],
  );

  // Toolbar filter cascade  (Org → Div → Dept)
  const divOptions = useMemo(
    () =>
      filterOrgId
        ? parentOptions.filter(
            (o) => o.orgType === "DIVISION" && o.parentId === filterOrgId,
          )
        : [],
    [parentOptions, filterOrgId],
  );
  const deptOptions = useMemo(
    () =>
      filterDivId
        ? parentOptions.filter(
            (o) => o.orgType === "DEPARTMENT" && o.parentId === filterDivId,
          )
        : [],
    [parentOptions, filterDivId],
  );

  // Form picker cascade  (Org → Div → Dept)
  const formDivOptions = useMemo(
    () =>
      formOrgId
        ? parentOptions.filter(
            (o) => o.orgType === "DIVISION" && o.parentId === formOrgId,
          )
        : [],
    [parentOptions, formOrgId],
  );
  const formDeptOptions = useMemo(
    () =>
      formDivId
        ? parentOptions.filter(
            (o) => o.orgType === "DEPARTMENT" && o.parentId === formDivId,
          )
        : [],
    [parentOptions, formDivId],
  );

  // ─── Filter ──────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return applications.filter((app) => {
      // Text search
      const matchesSearch =
        !q ||
        app.name.toLowerCase().includes(q) ||
        (app.appCode || "").toLowerCase().includes(q) ||
        (app.departmentName || "").toLowerCase().includes(q) ||
        (app.divisionName || "").toLowerCase().includes(q);

      // Org filter: keep apps whose division is a child of the selected org
      const matchesOrg =
        !filterOrgId || divOptions.some((d) => d.id === app.divisionId);

      // Division filter (second level)
      const matchesDiv = !filterDivId || app.divisionId === filterDivId;

      // Department filter (third level)
      const matchesDept = !filterDeptId || app.departmentId === filterDeptId;

      return matchesSearch && matchesOrg && matchesDiv && matchesDept;
    });
  }, [
    applications,
    searchQuery,
    filterOrgId,
    filterDivId,
    filterDeptId,
    divOptions,
  ]);

  // ─── Columns ─────────────────────────────────────────────────────────────

  const columns: Column<CMDBApplication>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => <span className="d text-sm">{i + 1}</span>,
    },
    {
      key: "name",
      header: "App Name",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      key: "appCode",
      header: "App Code",
      align: "center",
      render: (row) =>
        row.appCode ? (
          <span className="inline-flex items-center justify-center w-20 px-2 py-0.5 rounded-md bg-muted text-xs font-mono text-foreground truncate">
            {row.appCode}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "departmentName",
      header: "Department",
      align: "left",
      render: (row) => (
        <span className="text-sm d">{row.departmentName || "—"}</span>
      ),
    },
    {
      key: "divisionName",
      header: "Division",
      align: "left",
      render: (row) => (
        <span className="text-sm d">{row.divisionName || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} pill />,
    },
    {
      key: "criticality",
      header: "Criticality",
      align: "center",
      render: (row) => (
        <StatusBadge status={row.criticality} type="criticality" pill />
      ),
    },
    {
      key: "componentCount",
      header: "Components",
      align: "center",
      render: (row) => (
        <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 text-xs font-medium">
          {row.componentCount ?? 0}
        </span>
      ),
    },
    // {
    //   key: "ssps",
    //   header: "SSP",
    //   align: "center",
    //   cellClassName: "text-left [&>div]:justify-start",
    //   render: (row) => {
    //     const ssps = row.ssps ?? [];
    //     if (ssps.length === 0) return <span className="d">—</span>;
    //     return (
    //       <div className="flex flex-col items-start gap-0.5">
    //         {ssps.map((s) => (
    //           <button
    //             key={s.sspId}
    //             onClick={(e) => { e.stopPropagation(); navigate(`/ssp/${s.sspId}`); }}
    //             title={s.title}
    //             className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 underline-offset-2 hover:underline max-w-[220px] truncate text-left"
    //           >
    //             {s.title}
    //           </button>
    //         ))}
    //       </div>
    //     );
    //   },
    // },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-20",
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <RBACAction module="cmdb" action="update">
            <Tip label="Edit" side="top">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(row);
                }}
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Edit2 className="size-4 d hover:text-foreground" />
              </button>
            </Tip>
          </RBACAction>
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete Application" side="top">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingApp(row);
                }}
                className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Applications</h1>
          <p className="text-sm d mt-0.5">
            Manage CMDB applications, their status, criticality, and component
            mappings
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="size-4" />
              Add New
            </button>
          </RBACMultiAction>
        </div>
      </div>

      {/* Toolbar: Search + Cascade Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
          <input
            type="text"
            placeholder="Search applications"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
          />
        </div>

        <OrgCascadeFilter
          parentOptions={parentOptions}
          orgId={filterOrgId}
          divId={filterDivId}
          deptId={filterDeptId}
          onOrgChange={setFilterOrgId}
          onDivChange={setFilterDivId}
          onDeptChange={setFilterDeptId}
          disabled={loading}
        />

        {/* Clear filters */}
        {(filterOrgId || filterDivId || filterDeptId || searchQuery) && (
          <button
            onClick={() => {
              setFilterOrgId("");
              setFilterDivId("");
              setFilterDeptId("");
              setSearchQuery("");
            }}
            className="px-3 py-2 text-sm d hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filtered.slice(page * pageSize, (page + 1) * pageSize)}
        loading={loading}
        emptyText="No applications found"
        page={page}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/cmdb/applications/${row.id}`)}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingApp ? "Edit Application" : "New Application"}
        size="2xl"
        footer={
          <ModalFooterButtons
            onCancel={closeModal}
            onSubmit={handleSubmit}
            submitLabel="Save"
            submitDisabled={submitting}
          />
        }
      >
        <div className="space-y-3">
          {/* ── Section: Basic Information (all required) ── */}
          <div className="space-y-2.5">
            <p className="text-[10px] font-semibold tracking-widest d">
              Basic Information
            </p>
            {/* Row 1: Application Name + App Code (identity fields together, both required) */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Application Name" required>
                <FormInput
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  placeholder="e.g. Internet Banking Portal"
                />
              </FormField>
              <FormField label="Application Code" required>
                <FormInput
                  value={form.appCode}
                  onChange={(v) => setForm({ ...form, appCode: v })}
                  placeholder="e.g. IB, CBS, PAYROLL"
                />
              </FormField>
            </div>
            {/* Row 2: Status + Criticality (state fields together, both required) */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Status" required>
                <FormSelect
                  value={form.status}
                  onChange={(v) =>
                    setForm({ ...form, status: v as ApplicationStatus })
                  }
                  options={[
                    { value: "", label: "Select status" },
                    { value: "OPERATIONAL", label: "Operational" },
                    { value: "UNDER_DEVELOPMENT", label: "Under Development" },
                    { value: "DISPOSITION", label: "Disposition" },
                    { value: "OTHER", label: "Other" },
                  ]}
                />
              </FormField>
              <FormField label="Criticality" required>
                <FormSelect
                  value={form.criticality}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      criticality: v as ApplicationCriticality,
                    })
                  }
                  options={[
                    { value: "", label: "Select criticality" },
                    { value: "LOW", label: "Low" },
                    { value: "MEDIUM", label: "Medium" },
                    { value: "HIGH", label: "High" },
                    { value: "CRITICAL", label: "Critical" },
                  ]}
                />
              </FormField>
            </div>
          </div>

          {/* ── Section: Organization Hierarchy (create only, all required) ── */}
          {!editingApp && (
            <div className="space-y-2.5 border-t border-border pt-3">
              <p className="text-[10px] font-semibold tracking-widest d">
                Organization Hierarchy
              </p>
              {/* Row 1: Organization + Division */}
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Organization" required>
                  <FormSelect
                    value={formOrgId}
                    onChange={(v) => {
                      setFormOrgId(v);
                      setFormDivId("");
                      setFormDeptId("");
                      setForm({ ...form, parentId: "" });
                    }}
                    options={[
                      { value: "", label: "Select organization" },
                      ...orgOptions.map((o) => ({
                        value: o.id,
                        label: o.name,
                      })),
                    ]}
                  />
                </FormField>
                <FormField label="Division" required>
                  <FormSelect
                    value={formDivId}
                    onChange={(v) => {
                      setFormDivId(v);
                      setFormDeptId("");
                      setForm({ ...form, parentId: "" });
                    }}
                    options={[
                      {
                        value: "",
                        label: formOrgId
                          ? "Select division"
                          : "Select an organization first",
                      },
                      ...formDivOptions.map((o) => ({
                        value: o.id,
                        label: o.name,
                      })),
                    ]}
                  />
                </FormField>
              </div>
              {/* Row 2: Department (full width — cascade hint needs space) */}
              <FormField
                label="Department"
                required
                hint="Applications must be created under a Department"
              >
                <FormSelect
                  value={formDeptId}
                  onChange={(v) => {
                    setFormDeptId(v);
                    setForm({ ...form, parentId: v });
                  }}
                  options={[
                    {
                      value: "",
                      label: formDivId
                        ? "Select department"
                        : "Select a division first",
                    },
                    ...formDeptOptions.map((o) => ({
                      value: o.id,
                      label: o.name,
                    })),
                  ]}
                />
              </FormField>
            </div>
          )}

          {/* ── Section: Additional (optional) ── */}
          <div className="border-t border-border pt-3">
            <p className="text-[10px] font-semibold tracking-widest d mb-2.5">
              Additional{" "}
              <span className="normal-case font-normal d/50">(optional)</span>
            </p>
            <FormField label="Description">
              <FormTextarea
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                placeholder="Optional description"
                rows={3}
              />
            </FormField>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deletingApp}
        onClose={() => {
          if (!deleting) setDeletingApp(null);
        }}
        title="Delete Application?"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setDeletingApp(null)}
            onSubmit={confirmDelete}
            submitLabel={deleting ? "Deleting" : "Delete"}
            submitDisabled={deleting}
          />
        }
      >
        <p className="text-sm d">
          This will permanently remove all CMDB data linked to this application
          - components, assets, cloud accounts, and service mappings. This
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
